import React, { useState } from "react";
import { Button } from "@heroui/button";
import {
  Card,
  cn,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@heroui/react";
import {
  HandThumbsUp,
  HandThumbsDown,
  ChatDots,
  Send,
  StarFill,
} from "react-bootstrap-icons";

import { useZodForm, getFieldProps } from "@/lib/form-utils";
import {
  FeedbackPhase2FormSchema,
  FeedbackPhase2FormData,
} from "@/lib/form-schemas";

interface FeedbackWidgetProps {
  className?: string;
}

export default function FeedbackWidget({ className }: FeedbackWidgetProps) {
  const [phase1Rating, setPhase1Rating] = useState<
    "positive" | "negative" | null
  >(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}`);

  // React Hook Form for phase 2 feedback
  const feedbackForm = useZodForm(FeedbackPhase2FormSchema, {
    defaultValues: {
      whatWorked: [],
      whatDidntWork: [],
      additionalComments: "",
    },
  });

  const submitFeedback = async (feedback: {
    phase1: "positive" | "negative";
    phase2?: {
      whatWorked?: string[];
      whatDidntWork?: string[];
      additionalComments?: string;
    };
  }) => {
    try {
      await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...feedback,
          sessionId,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch {
      // Don't block the UI if feedback submission fails
      // Error is logged server-side
    }
  };

  const handlePhase1Feedback = async (rating: "positive" | "negative") => {
    setPhase1Rating(rating);

    // Send phase 1 feedback immediately
    const feedback = { phase1: rating };

    await submitFeedback(feedback);

    // Open modal for phase 2
    setIsModalOpen(true);
  };

  const handlePhase2Submit = feedbackForm.handleSubmit(
    async (data: FeedbackPhase2FormData) => {
      if (!phase1Rating) return;

      // Send complete feedback with phase 2 details
      const feedback = {
        phase1: phase1Rating,
        phase2: {
          whatWorked: data.whatWorked || [],
          whatDidntWork: data.whatDidntWork || [],
          additionalComments: data.additionalComments?.trim() || undefined,
        },
      };

      await submitFeedback(feedback);
      setIsModalOpen(false);
      setIsSubmitted(true);
    },
  );

  const handlePhase2SubmitClick = () => {
    handlePhase2Submit();
  };

  const handleSkipPhase2 = () => {
    setIsModalOpen(false);
    setIsSubmitted(true);
  };

  // Watch form values for reactivity
  const whatWorked = feedbackForm.watch("whatWorked") || [];
  const whatDidntWork = feedbackForm.watch("whatDidntWork") || [];

  const toggleWorkedItem = (item: string) => {
    const currentWorked = feedbackForm.getValues("whatWorked") || [];
    const newWorked = currentWorked.includes(item)
      ? currentWorked.filter((i) => i !== item)
      : [...currentWorked, item];

    feedbackForm.setValue("whatWorked", newWorked);
  };

  const toggleDidntWorkItem = (item: string) => {
    const currentDidntWork = feedbackForm.getValues("whatDidntWork") || [];
    const newDidntWork = currentDidntWork.includes(item)
      ? currentDidntWork.filter((i) => i !== item)
      : [...currentDidntWork, item];

    feedbackForm.setValue("whatDidntWork", newDidntWork);
  };

  const workedOptions = [
    "Clear recommendations",
    "Easy to understand",
    "Relevant suggestions",
    "Good variety of options",
    "Helpful explanations",
    "Quick process",
  ];

  const didntWorkOptions = [
    "Too many options",
    "Not relevant to my needs",
    "Missing information",
    "Too complex",
    "Too basic",
    "Slow process",
  ];

  if (isSubmitted) {
    return (
      <div className={cn("mt-12 mb-8", className)}>
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 bg-gradient-to-br from-green-900/30 to-emerald-900/20 border border-green-500/20 backdrop-blur-sm">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center w-16 h-16 mb-6">
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-pulse" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-xl font-bold">✓</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-green-400 mb-3">
                Thank You - Your Voice Matters! 🦙
              </h3>
              <p className="text-green-300/80 max-w-md mx-auto leading-relaxed">
                Every piece of feedback helps us build better security tools for
                the community. We read every comment and use your insights to
                prioritize improvements!
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Phase 1 - Elegant feedback section */}
      <div className={cn("mt-12 mb-8", className)}>
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mb-4">
              <StarFill className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              How Was Your Experience?
            </h3>
            <p className="text-gray-300 max-w-lg mx-auto leading-relaxed">
              Your feedback is invaluable! As cybersecurity professionals
              building for the community, we genuinely want to hear from you to
              make this better.
            </p>
          </div>

          {/* Feedback Card */}
          <Card className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/60 border border-slate-600/30 backdrop-blur-sm">
            <div className="text-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                <Button
                  className="group relative  bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-green-500/20 flex items-center gap-3 min-w-[160px]"
                  size="lg"
                  onPress={() => handlePhase1Feedback("positive")}
                >
                  <div className="relative">
                    <HandThumbsUp className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200" />
                  </div>
                  <span>Loved it!</span>
                </Button>

                <Button
                  className="group relative bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-orange-500/20 flex items-center gap-3 min-w-[160px]"
                  size="lg"
                  onPress={() => handlePhase1Feedback("negative")}
                >
                  <div className="relative">
                    <HandThumbsDown className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-200" />
                  </div>
                  <span>Could be better</span>
                </Button>
              </div>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                <div className="w-1 h-1 bg-gray-400 rounded-full" />
                <span>
                  Your feedback is anonymous, welcomed, and directly impacts our
                  roadmap
                </span>
                <div className="w-1 h-1 bg-gray-400 rounded-full" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Phase 2 Modal - Enhanced Design */}
      <Modal
        backdrop="blur"
        isOpen={isModalOpen}
        scrollBehavior="inside"
        size="3xl"
        onClose={() => setIsModalOpen(false)}
      >
        <ModalContent className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-600/30">
          <ModalHeader className="flex flex-col gap-1 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                <ChatDots className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  We Value Your Input! (Optional)
                </h2>
                <p className="text-gray-400 text-sm">
                  Every detail helps us create better cybersecurity tools for
                  the community
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="py-6">
            <div className="space-y-8">
              {phase1Rating === "positive" && (
                <div className="bg-green-900/20 border border-green-500/20 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                    <span className="text-xl">🎉</span>
                    What worked well for you?
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {workedOptions.map((option) => (
                      <Button
                        key={option}
                        className={cn(
                          "text-left justify-start py-3 px-4 text-sm rounded-lg transition-all duration-200 border",
                          whatWorked.includes(option)
                            ? "bg-green-600 border-green-500 text-white shadow-lg shadow-green-600/20"
                            : "bg-slate-700/50 border-slate-600/30 text-gray-300 hover:bg-slate-600/50 hover:border-slate-500/50",
                        )}
                        variant="flat"
                        onPress={() => toggleWorkedItem(option)}
                      >
                        <span className="flex items-center gap-2">
                          {whatWorked.includes(option) && (
                            <span className="text-green-200">✓</span>
                          )}
                          {option}
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {phase1Rating === "negative" && (
                <div className="bg-orange-900/20 border border-orange-500/20 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-orange-400 mb-4 flex items-center gap-2">
                    <span className="text-xl">🤔</span>
                    What could we improve?
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {didntWorkOptions.map((option) => (
                      <Button
                        key={option}
                        className={cn(
                          "text-left justify-start py-3 px-4 text-sm rounded-lg transition-all duration-200 border",
                          whatDidntWork.includes(option)
                            ? "bg-orange-600 border-orange-500 text-white shadow-lg shadow-orange-600/20"
                            : "bg-slate-700/50 border-slate-600/30 text-gray-300 hover:bg-slate-600/50 hover:border-slate-500/50",
                        )}
                        variant="flat"
                        onPress={() => toggleDidntWorkItem(option)}
                      >
                        <span className="flex items-center gap-2">
                          {whatDidntWork.includes(option) && (
                            <span className="text-orange-200">✓</span>
                          )}
                          {option}
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2">
                  <span className="text-xl">💭</span>
                  Any additional thoughts?
                </h4>
                <Textarea
                  {...getFieldProps(feedbackForm, "additionalComments")}
                  className="w-full"
                  classNames={{
                    base: "bg-slate-700/50 border border-slate-600/30",
                    input: "text-white placeholder-gray-400",
                  }}
                  placeholder="Tell us anything else that could help improve the experience..."
                  rows={4}
                />

                {/* GDPR Privacy Notice */}
                <div className="bg-gray-500/5 border-l-4 border-gray-400 p-3 rounded mt-3">
                  <p className="text-xs text-gray-400">
                    <strong>Privacy Notice:</strong> Please avoid including
                    personal information (email, phone, etc.) in your feedback.
                    Your feedback is welcomed and will be used solely to improve
                    our service and will not be shared with third parties.
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="pt-6">
            <div className="flex gap-3 w-full">
              <Button
                className="flex-1 bg-slate-700/50 hover:bg-slate-600/50 text-gray-300 font-medium py-3 rounded-lg transition-all duration-200 border border-slate-600/30"
                variant="flat"
                onPress={handleSkipPhase2}
              >
                Skip for now
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
                variant="solid"
                onPress={handlePhase2SubmitClick}
              >
                <Send className="w-4 h-4" />
                Send Feedback
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
